import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of, Subject, timer } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { TerminalService } from '@shared/services';
import { AutosizeDirective } from '@shared/directives';

@Component({
  selector: 'app-rbnk-terminal',
  templateUrl: 'rbnk-terminal.component.html',
  styleUrls: ['rbnk-terminal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AutosizeDirective],
})
export class RbnkTerminalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('terminalContainerRef') private terminalContainerRef!: ElementRef;

  @ViewChild('textareaRef') private textareaRef!: ElementRef;

  historyItems: string[] = [];

  activeHistoryItem = 0;

  isTopFirst = false;

  isBottomFirst = false;

  private destroy$ = new Subject<void>();

  busy = this.terminalService.busy;

  // Seconds since the command in flight was sent, ticking while busy
  elapsedSeconds = toSignal(
    toObservable(this.busy).pipe(
      switchMap((busy) =>
        busy ? timer(0, 1000).pipe(map(() => Math.floor((Date.now() - this.terminalService.commandStartedAt) / 1000))) : of(0),
      ),
    ),
    { initialValue: 0 },
  );

  constructor(private terminalService: TerminalService) {}

  ngOnInit(): void {
    this.terminalService.responseCalled$.pipe(takeUntil(this.destroy$)).subscribe((template) => {
      const outputWindow = document.querySelector('.output-window');
      if (outputWindow != null) {
        outputWindow.insertAdjacentHTML('beforeend', template);
        this.scrollToEnd();
        this.focusInputLine();
      }
    });

    this.terminalService.terminalHistory.forEach((historyItem: string) => {
      const outputWindow = document.querySelector('.output-window');
      if (outputWindow != null) {
        outputWindow.insertAdjacentHTML('beforeend', historyItem);
      }
    });
  }

  ngAfterViewInit() {
    this.focusInputLine();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent): void {
    // Ctrl+C is also "copy" on Windows/Linux, so only treat it as detach when nothing is selected
    const hasSelection = !!window.getSelection()?.toString();
    if (this.busy() && event.ctrlKey && event.key === 'c' && !hasSelection) {
      event.preventDefault();
      this.terminalService.detach();
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();

      if (this.busy()) {
        return;
      }

      const command: string = this.textareaRef.nativeElement.value.trim();

      this.textareaRef.nativeElement.value = '';

      const outputWindow = document.querySelector('.output-window');

      if (outputWindow != null) {
        const template = '<div class="prev-command"><div class="terminal-prompt">$ > </div>' + command + '</div>';
        outputWindow.insertAdjacentHTML('beforeend', template);
        this.terminalService.terminalHistory.push(template);
      }

      this.historyItems.push(command);

      this.scrollToEnd();

      if (command) {
        this.terminalService.sendCmd(command);
      }
    }
  }

  onKeyUp(event: KeyboardEvent): void {
    if (event.key == 'ArrowUp') {
      this.onPressArrowUp();
    } else if (event.key == 'ArrowDown') {
      this.onPressArrowDown();
    } else if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
      this.isTopFirst = false;
      this.isBottomFirst = false;
    } else {
      this.activeHistoryItem = 0;
      this.isTopFirst = false;
      this.isBottomFirst = false;
    }
  }

  onPressArrowUp(): void {
    const selectionStart = this.textareaRef.nativeElement.selectionStart;
    const selectionEnd = this.textareaRef.nativeElement.selectionEnd;
    const zeroSelectionLength = selectionStart === selectionEnd;

    if (selectionStart === 0 && zeroSelectionLength && this.historyItems.length) {
      if ((this.historyItems.length > 0 && this.isTopFirst) || (this.historyItems.length > 0 && !this.textareaRef.nativeElement.value)) {
        this.activeHistoryItem = this.historyItems.length !== this.activeHistoryItem ? this.activeHistoryItem + 1 : this.activeHistoryItem;

        this.textareaRef.nativeElement.value = this.historyItems[this.historyItems.length - this.activeHistoryItem];
        this.textareaRef.nativeElement.selectionEnd = 0;
        this.isTopFirst = false;
      }
      this.isTopFirst = true;
    }
  }

  onPressArrowDown(): void {
    const selectionStart = this.textareaRef.nativeElement.selectionStart;
    const selectionEnd = this.textareaRef.nativeElement.selectionEnd;
    const zeroSelectionLength = selectionStart === selectionEnd;

    if (selectionEnd === this.textareaRef.nativeElement.value.length && zeroSelectionLength && this.historyItems.length) {
      if (this.activeHistoryItem > 0 && this.isBottomFirst) {
        if (this.activeHistoryItem > 0) {
          this.activeHistoryItem -= 1;
          this.textareaRef.nativeElement.value =
            this.activeHistoryItem === 0 ? '' : this.historyItems[this.historyItems.length - this.activeHistoryItem];
        }

        this.isBottomFirst = false;
      }

      this.isBottomFirst = true;
    }
  }

  focusInputLine(): void {
    if (!this.textareaRef?.nativeElement) {
      return;
    }

    const selectionStart = this.textareaRef.nativeElement.selectionStart;
    const selectionEnd = this.textareaRef.nativeElement.selectionEnd;
    const zeroSelectionLength = selectionStart === selectionEnd;
    if (!zeroSelectionLength) {
      return;
    }

    setTimeout(() => this.textareaRef.nativeElement.focus(), 100);
  }

  scrollToEnd(): void {
    this.terminalContainerRef.nativeElement.scroll({
      top: this.terminalContainerRef.nativeElement.scrollHeight,
      left: 0,
      behavior: 'smooth',
    });
  }
}
